import React, { useState } from "react";
import emailjs from "emailjs-com";
import { Send, Upload, Info, CheckCircle2, AlertCircle } from "lucide-react";
import SEO from "./SEO";
import "./Form.css";

const Form = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    experience: "",
    reason: "",
    resumeLink: "",
    jobRole: "",
  });

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.resumeLink) {
      showAlert("Please provide a Google Drive link to your resume.", "error");
      setIsSubmitting(false);
      return;
    }

    const templateParams = {
      to_name: "Admin",
      from_name: `${formData.firstName} ${formData.lastName}`,
      phone: formData.phone,
      email: formData.email,
      experience: formData.experience,
      reason: formData.reason,
      resumeLink: formData.resumeLink,
      jobRole: formData.jobRole,
    };

    emailjs
      .send(
        "service_g9a9o6f",
        "template_82j5m7y",
        templateParams,
        "4r1JgZq5u3u9r_fW9"
      )
      .then(
        (response) => {
          console.log("SUCCESS!", response.status, response.text);
          showAlert("Application submitted successfully! We'll be in touch soon.", "success");
          setIsSubmitting(false);
          setFormData({
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            experience: "",
            reason: "",
            resumeLink: "",
            jobRole: "",
          });
        },
        (error) => {
          console.error("FAILED...", error);
          showAlert("Submission failed. Please check your connection and try again.", "error");
          setIsSubmitting(false);
        }
      );
  };

  return (
    <div className="job-modern-form-page">
      <SEO
        title="Apply for Careers — D Base Solutions Pvt Ltd"
        description="Submit your application to join D Base Solutions. Send your resume and profile details for open positions."
        keywords="D Base Solutions job application, apply software developer Eluru, join DBS"
        canonical="https://dbasesolutions.in/form"
        ogImage="/og-careers.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Careers", url: "/careers" },
          { name: "Apply", url: "/form" }
        ]}
      />
      {/* Dynamic Background */}
      <div className="bg-shapes">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>

      <div className="container">
        <div className="form-wrapper">
          {alertMessage && (
            <div className={`modern-alert ${alertType}`}>
              {alertType === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
              {alertMessage}
            </div>
          )}

          <div className="form-header-modern">
            <h2 className="form-title-modern">Start Your <span className="text-gradient">Journey</span></h2>
            <p className="form-subtitle-modern">Complete the form below to apply for your dream role at D Base Solutions.</p>
          </div>

          <form className="modern-application-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="input-field">
                <label>First Name *</label>
                <input type="text" name="firstName" placeholder="John" required value={formData.firstName} onChange={handleChange} />
              </div>
              <div className="input-field">
                <label>Last Name *</label>
                <input type="text" name="lastName" placeholder="Doe" required value={formData.lastName} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="input-field">
                <label>Phone Number *</label>
                <input type="tel" name="phone" placeholder="+91 00000 00000" required value={formData.phone} onChange={handleChange} />
              </div>
              <div className="input-field">
                <label>Email Address *</label>
                <input type="email" name="email" placeholder="john@example.com" required value={formData.email} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="input-field">
                <label>Total Experience (Years) *</label>
                <input type="number" name="experience" placeholder="e.g. 2" required value={formData.experience} onChange={handleChange} />
              </div>
              <div className="input-field">
                <label>Job Role *</label>
                <select name="jobRole" required value={formData.jobRole} onChange={handleChange}>
                  <option value="">Select Role</option>
                  <option value="Business Executive">Business Executive</option>
                  <option value="Software Developer">Software Developer</option>
                  <option value=".NET Developer">.NET Developer</option>
                  <option value="Data Entry Operator">Data Entry Operator</option>
                </select>
              </div>
            </div>

            <div className="input-field full-row">
              <label>Resume Link (Google Drive) *</label>
              <div className="input-with-icon">
                <Upload size={18} className="field-icon" />
                <input type="url" name="resumeLink" placeholder="Paste public shareable link" required value={formData.resumeLink} onChange={handleChange} />
              </div>
              <p className="field-note"><Info size={14} /> Ensure your drive link is set to "Anyone with the link".</p>
            </div>

            <div className="input-field full-row">
              <label>Tell us why you want to join DBS *</label>
              <textarea name="reason" placeholder="Describe your passion and what you bring to the team..." required value={formData.reason} onChange={handleChange} rows="4" />
            </div>

            <button type="submit" className="modern-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : (
                <>
                  Submit Application <Send size={18} />
                </>
              )}
            </button>

            <p className="form-disclaimer">
              By submitting, you agree to our privacy policy and consent to data processing for recruitment.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Form;
